import {ChangeEvent, forwardRef, useCallback, useEffect, useRef} from 'react'
import Form from 'react-bootstrap/Form'
import {useLoadGoogleMaps} from '../hooks/useLoadGoogleMaps'

type FormControlProps = React.ComponentProps<typeof Form.Control>

export type AddressComponent = {
  long_name?: string
  short_name?: string
  types: string[]
}

export interface PlaceSelection {
  address?: string
  placeId?: string
  latLng?: {lat: number; lng: number}
  addressComponents?: AddressComponent[]
}

interface GooglePlacesAutocompleteInputProps extends FormControlProps {
  onPlaceSelect?: (selection: PlaceSelection) => void
}

export const GooglePlacesAutocompleteInput = forwardRef<HTMLInputElement, GooglePlacesAutocompleteInputProps>(
  ({onPlaceSelect, onChange, ...rest}, forwardedRef) => {
    const {isLoaded, loadError} = useLoadGoogleMaps()
    const inputRef = useRef<HTMLInputElement | null>(null)
    const autocompleteRef = useRef<any>(null)
    const onPlaceSelectRef = useRef(onPlaceSelect)

    useEffect(() => {
      onPlaceSelectRef.current = onPlaceSelect
    }, [onPlaceSelect])

    const assignRefs = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node
        if (!forwardedRef) return
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else {
          forwardedRef.current = node
        }
      },
      [forwardedRef],
    )

    useEffect(() => {
      if (!isLoaded || !inputRef.current || autocompleteRef.current) return
      const googleApi = (window as typeof window & {google?: any}).google
      if (!googleApi?.maps?.places?.Autocomplete) return

      const autocomplete = new googleApi.maps.places.Autocomplete(inputRef.current, {
        fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
        types: ['address'],
        componentRestrictions: { country: 'us' },
      })
      autocompleteRef.current = autocomplete

      const extractLatLng = (location: any) => {
        if (!location) return undefined
        if (typeof location.lat === 'function' && typeof location.lng === 'function') {
          return {lat: location.lat(), lng: location.lng()}
        }
        if (typeof location.lat === 'number' && typeof location.lng === 'number') {
          return {lat: location.lat, lng: location.lng}
        }
        return undefined
      }

      const handlePlaceSelection = () => {
        const place = autocomplete.getPlace()
        if (!place) return

        const formattedAddress =
          place.formatted_address ?? place.name ?? inputRef.current?.value
        if (formattedAddress && inputRef.current) {
          inputRef.current.value = formattedAddress
        }

        const callback = onPlaceSelectRef.current
        if (callback) {
          const location =
            place.geometry?.location ??
            (place as unknown as {lat?: number; lng?: number})

          callback({
            address: formattedAddress ?? inputRef.current?.value,
            placeId: place.place_id,
            latLng: extractLatLng(location),
            addressComponents: place.address_components as AddressComponent[] | undefined,
          })
        }
      }

      const listener = autocomplete.addListener('place_changed', handlePlaceSelection)

      return () => {
        listener.remove()
        autocompleteRef.current = null
      }
    }, [isLoaded])

    useEffect(() => {
      if (loadError) {
        console.warn('[GooglePlacesAutocompleteInput] Unable to load Google Maps script', loadError)
      }
    }, [loadError])

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange?.(event)
      },
      [onChange],
    )

    return (
      <Form.Control {...rest} ref={assignRefs} onChange={handleChange} autoComplete='off' />
    )
  },
)

GooglePlacesAutocompleteInput.displayName = 'GooglePlacesAutocompleteInput'
